/**
 * useThreadActivity — the controller behind the mentions tab (2026-09-17).
 *
 * The same seam shape as `useThreadSearchController`: the HOST owns every piece
 * of data access and hands it in as a `ThreadActivityApi`, so this hook holds no
 * Firestore, no auth and no clock. That is what lets the whole of the list's
 * behaviour — the filter, the unread arithmetic, opening a row, marking read,
 * and the batched context fetch — be exercised in a test with three plain
 * functions and no emulator.
 *
 * The context fetch is the only interesting part. Rows are grouped into one
 * window PER THREAD (`planContextWindows`) and fetched together; each row then
 * slices its own neighbours out of its thread's window. A thirty-row list across
 * four threads costs four reads, not thirty. Windows are cached by
 * thread+radius+bounds for the life of the hook, so re-rendering, toggling the
 * filter back and forth, or expanding a row twice re-reads nothing.
 *
 * Every failure is quiet and one-directional: a window that cannot be read
 * leaves the row with its own stored snippet, which is always present. Nothing
 * here can leave a row blank.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MENTION_CONTEXT_RADIUS_COLLAPSED,
  MENTION_CONTEXT_RADIUS_EXPANDED,
  isMentionRetired,
  planContextWindows,
  selectMentions,
  sliceContextAround,
  type MentionContextMessage,
  type MentionFilter,
} from '../../services/threadMentions/mentionInboxModel';
import {
  resolveThreadClosure,
  type ThreadClosure,
  type ThreadClosureFacts,
} from '../../services/groupThreads/threadClosure';
import {
  EMPTY_MENTION_READ_STATE,
  markAllMentionsRead,
  markMentionRead,
  type MentionReadState,
  type ThreadMention,
} from '../../types/threadMention';
import type { MentionRowContext } from './MentionsList';

export interface ThreadActivityApi {
  /** Every mention row the browser holds, newest first (the host's listener). */
  mentions: readonly ThreadMention[];
  /** The viewer's own read state (the host's listener). */
  readState: MentionReadState;
  /** The workspace the chat is in — rows from elsewhere are withheld. */
  accountId: string | null;
  /** Persist a new read state. Best-effort; a false is not surfaced. */
  saveReadState: (state: MentionReadState) => void | Promise<unknown>;
  /** Open a thread and land on a message (the search hit's own path). */
  openMention: (row: ThreadMention) => void;
  /** One bounded window of a thread's shared transcript. */
  fetchWindow: (threadId: string, fromOrderTs: number, toOrderTs: number) => Promise<MentionContextMessage[]>;
  /**
   * Threads the viewer can no longer open, with the facts a surface renders the
   * explanation from. Host-owned (useMentionThreadClosure); an empty map is the
   * lever's off position and restores the previous behaviour exactly.
   */
  closedThreads: ReadonlyMap<string, ThreadClosureFacts>;
  /** The viewer's own uid — the only input "you archived this" needs. */
  viewerUid: string | null;
}

export interface ThreadActivityController {
  filter: MentionFilter;
  setFilter: (filter: MentionFilter) => void;
  /** The rows to render — filtered, workspace-scoped and capped. */
  visible: ThreadMention[];
  /** Unread among the PEOPLE rows, which is what the tab badge counts. */
  unreadCount: number;
  contextByMentionId: Record<string, MentionRowContext>;
  expandedIds: Set<string>;
  toggleExpanded: (id: string) => void;
  open: (row: ThreadMention) => void;
  markAllRead: () => void;
  /**
   * The ONE denominator for "this row is retired". Handed to MentionsList so
   * the badge and all four places that component reads unread share a single
   * predicate — see the unreadCount note below for why that matters.
   */
  retiredThreadIds: ReadonlySet<string>;
  /** The resolved verdict per retired row. Absent id ⇒ an ordinary row. */
  closureByMentionId: Record<string, ThreadClosure>;
}

/** A fetched window, keyed so an identical request is never re-issued. */
type WindowCache = Map<string, MentionContextMessage[]>;

function windowKey(threadId: string, from: number, to: number): string {
  return `${threadId}|${from}|${to}`;
}

export function useThreadActivity(api: ThreadActivityApi): ThreadActivityController {
  const [filter, setFilter] = useState<MentionFilter>('people');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [windows, setWindows] = useState<Record<string, MentionContextMessage[]>>({});
  const cacheRef = useRef<WindowCache>(new Map());
  const inFlightRef = useRef<Set<string>>(new Set());

  // The live inputs ride a ref so the fetch effect below depends on the rows it
  // must fetch for, never on the identity of the host's callbacks — a keystroke
  // elsewhere in the chat must not re-issue every window.
  const apiRef = useRef(api);
  apiRef.current = api;

  const visible = useMemo(
    () => selectMentions({ rows: api.mentions, filter, accountId: api.accountId }),
    [api.mentions, filter, api.accountId],
  );

  const retiredThreadIds = useMemo(
    () => new Set(api.closedThreads.keys()),
    [api.closedThreads],
  );

  const closureByMentionId = useMemo(() => {
    const out: Record<string, ThreadClosure> = {};
    for (const row of visible) {
      const closure = resolveThreadClosure(api.closedThreads.get(row.threadId), api.viewerUid);
      if (closure) out[row.id] = closure;
    }
    return out;
  }, [visible, api.closedThreads, api.viewerUid]);

  // The badge counts what the DEFAULT view shows — the people rows — so the
  // number on the tab and the list behind it can never disagree. Prest0n's own
  // mentions are reachable through the `all` toggle and never inflate it.
  //
  // RETIRED ROWS LEAVE THE COUNT (owner ruling 2026-09-21: "stop counting it").
  // A badge is a call to action and a retired row has no action, so counting it
  // makes the number un-clearable by DOING anything. The invariant above is
  // strengthened rather than weakened: every row the badge counts is a row the
  // list shows AND that can be opened. That is only true while MentionsList
  // drops the unread EMPHASIS from the same rows, which is why
  // `retiredThreadIds` is handed to it rather than recomputed there — the
  // alternative ships an absent badge above three gold-bordered rows and a
  // "mark all read" button offering to clear a count of zero.
  //
  // Stored read state is never touched: `markMentionRead` writes into a map
  // capped at MENTION_READ_MAP_CAP, so auto-marking retired rows read would
  // evict real entries AND assert someone read what they were never shown.
  const unreadCount = useMemo(() => {
    const people = selectMentions({ rows: api.mentions, filter: 'people', accountId: api.accountId });
    let n = 0;
    for (const row of people) {
      if (isMentionRetired(row, retiredThreadIds)) continue;
      if (row.orderTs <= api.readState.lastReadAtMs) continue;
      if (api.readState.read[row.id] === true) continue;
      n += 1;
    }
    return n;
  }, [api.mentions, api.accountId, api.readState, retiredThreadIds]);

  // One window per thread, for whatever radius the widest expanded row needs.
  //
  // A RETIRED ROW ISSUES NO NEW READ, and that is a real saving rather than
  // bookkeeping: for a removed participant those reads are rules-denied and
  // already yield nothing, and for an archived thread the window is still
  // READABLE (archiving removes nobody from the participant list), which is
  // precisely why nothing caught this before.
  //
  // What is NOT done here is evicting context already on screen. The closed map
  // resolves asynchronously, so dropping already-sliced anchors would render a
  // row with its two context lines and then take them away ~200ms later — and
  // it would contradict "the summary is still readable at rest". So: skip
  // retired anchors when PLANNING; leave contextByMentionId alone.
  const anyExpanded = visible.some((row) => expandedIds.has(row.id));
  const radius = anyExpanded ? MENTION_CONTEXT_RADIUS_EXPANDED : MENTION_CONTEXT_RADIUS_COLLAPSED;
  const openable = useMemo(
    () => visible.filter((row) => !isMentionRetired(row, retiredThreadIds)),
    [visible, retiredThreadIds],
  );
  const plan = useMemo(() => planContextWindows(openable, radius), [openable, radius]);
  // The plan's identity changes on every render (fresh objects), so the effect
  // keys on its VALUE — otherwise it would re-run forever.
  const planKey = plan.map((w) => windowKey(w.threadId, w.fromOrderTs, w.toOrderTs)).join(',');

  useEffect(() => {
    let cancelled = false;
    const pending = plan.filter((w) => {
      const key = windowKey(w.threadId, w.fromOrderTs, w.toOrderTs);
      return !cacheRef.current.has(key) && !inFlightRef.current.has(key);
    });
    if (pending.length === 0) return () => { cancelled = true; };
    for (const w of pending) inFlightRef.current.add(windowKey(w.threadId, w.fromOrderTs, w.toOrderTs));
    void (async () => {
      const fetched = await Promise.all(pending.map(async (w) => {
        const key = windowKey(w.threadId, w.fromOrderTs, w.toOrderTs);
        try {
          const messages = await apiRef.current.fetchWindow(w.threadId, w.fromOrderTs, w.toOrderTs);
          return { key, messages };
        } catch {
          // A window nobody could read is an empty one: the row keeps its own
          // snippet, which is the whole point of storing it.
          return { key, messages: [] as MentionContextMessage[] };
        } finally {
          inFlightRef.current.delete(key);
        }
      }));
      if (cancelled) return;
      for (const { key, messages } of fetched) cacheRef.current.set(key, messages);
      setWindows((prev) => {
        const next = { ...prev };
        for (const { key, messages } of fetched) next[key] = messages;
        return next;
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey]);

  const contextByMentionId = useMemo(() => {
    const out: Record<string, MentionRowContext> = {};
    for (const w of plan) {
      const key = windowKey(w.threadId, w.fromOrderTs, w.toOrderTs);
      const messages = windows[key];
      if (!messages) continue;
      for (const anchorId of w.anchorIds) {
        const rowRadius = expandedIds.has(anchorId)
          ? MENTION_CONTEXT_RADIUS_EXPANDED
          : MENTION_CONTEXT_RADIUS_COLLAPSED;
        out[anchorId] = sliceContextAround(messages, anchorId, rowRadius);
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey, windows, expandedIds]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const open = useCallback((row: ThreadMention) => {
    const current = apiRef.current;
    // A retired row opens nothing AND marks nothing read — it never opened, so
    // nothing was read, and claiming otherwise would erase the fact that this
    // person never saw it. The row is neither read nor unread; it is retired.
    // The component refuses the click too; this is the belt behind that.
    if (resolveThreadClosure(current.closedThreads.get(row.threadId), current.viewerUid)) return;
    // Read state first: opening it IS reading it, and a failed persist must not
    // stop the navigation the person actually asked for.
    const next = markMentionRead(current.readState ?? EMPTY_MENTION_READ_STATE, row);
    if (next !== current.readState) void current.saveReadState(next);
    current.openMention(row);
  }, []);

  const markAllRead = useCallback(() => {
    const current = apiRef.current;
    // Through the NEWEST row the browser holds, never `Date.now()`: a mention
    // filed while this click was in flight would otherwise be marked read
    // before anyone had seen it.
    let newest = current.readState?.lastReadAtMs ?? 0;
    for (const row of current.mentions) if (row.orderTs > newest) newest = row.orderTs;
    void current.saveReadState(markAllMentionsRead(newest));
  }, []);

  return {
    filter,
    setFilter,
    visible,
    unreadCount,
    contextByMentionId,
    expandedIds,
    toggleExpanded,
    open,
    markAllRead,
    retiredThreadIds,
    closureByMentionId,
  };
}
